import {useUser} from "UserProvider";
import {useMessages} from "i18n";
import {useLogout} from "nav/pro/useLogout";
import {type AppDestination, appDestinations} from "nav/appDestinations";

/**
 * Navigation adapter for the logged-in top bar (ui#96; was the rail's nav hook before the rail was
 * dropped) — the React seam of the epic-#26 separation. The RULE (which destinations exist and which
 * survive an un-activated account) lives in {@link appDestinations}; this hook only holds the React
 * dependencies: the user, the active catalog, and the shared logout action.
 *
 * The labels come from `messages.nav.destinations` (ui#332). They used to be hardcoded English in
 * this file, which is why the logged-in nav bar rendered English on `transferify.ro`.
 *
 * One label does not: Personalization became a destination in ui#1292 but its copy has lived at
 * `nav.branding` since ui#450, and the owner kept that key deliberately (`i18n/en/chrome.ts` says why).
 * Composing the map HERE rather than duplicating the string into `nav.destinations` is what keeps one
 * home for that copy — the visible pill, its `aria-label` and the palette command all read the same
 * key, so they cannot come to disagree. This adapter is the right place for it: the table takes labels
 * from its caller precisely so where a string lives is the React layer's problem, not the model's.
 */

export type {AppDestination};

export function useAppNavigation(): {destinations: AppDestination[]; logout: () => void} {
	const [user] = useUser();
	const {nav} = useMessages();
	const logout = useLogout();
	const labels = {...nav.destinations, personalization: nav.branding};
	return {destinations: appDestinations(labels, user.isActive), logout};
}
